import type { Application, Container } from 'pixi.js';

type LightingEffectContainer = Container & {
  id?: string;
  cleanup?: () => void;
};

interface LightSpeck {
  graphic: any;
  homeX: number;
  homeY: number;
  phase: number;
  radius: number;
}

const createGlowTexture = (PIXI: any) => {
  const size = 512;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const context = canvas.getContext('2d');
  if (!context) {
    return PIXI.Texture.WHITE;
  }

  const center = size / 2;
  const gradient = context.createRadialGradient(
    center,
    center,
    0,
    center,
    center,
    center
  );
  gradient.addColorStop(0, 'rgba(255, 252, 214, 0.95)');
  gradient.addColorStop(0.18, 'rgba(255, 231, 140, 0.62)');
  gradient.addColorStop(0.45, 'rgba(255, 184, 79, 0.22)');
  gradient.addColorStop(1, 'rgba(255, 184, 79, 0)');

  context.fillStyle = gradient;
  context.fillRect(0, 0, size, size);

  return PIXI.Texture.from(canvas);
};

const drawLightBeams = (
  beams: any,
  width: number,
  height: number,
  sourceX: number,
  sourceY: number,
  time: number
) => {
  const beamShapes = [
    { left: -0.06, right: 0.2, alpha: 0.11, phase: 0 },
    { left: 0.16, right: 0.42, alpha: 0.08, phase: 1.3 },
    { left: 0.45, right: 0.74, alpha: 0.07, phase: 2.2 },
  ];

  beams.clear();
  beamShapes.forEach((shape) => {
    const pulse = Math.sin(time * 1.2 + shape.phase) * 0.018;
    beams.beginFill(0xffe7a1, Math.max(0.04, shape.alpha + pulse));
    beams.moveTo(sourceX, sourceY);
    beams.lineTo(width * shape.left, height);
    beams.lineTo(width * shape.right, height);
    beams.closePath();
    beams.endFill();
  });
};

export const renderLightingEffect = async (
  app: Application | null,
  width: number,
  height: number,
  effectId: string,
  effectsSpriteRef: any
) => {
  const PIXI = await import('pixi.js');
  const sprites = new PIXI.Container() as LightingEffectContainer;
  sprites.id = effectId;
  sprites.blendMode = 'screen';

  effectsSpriteRef.current = sprites;
  effectsSpriteRef.current.id = effectId;

  const warmWash = new PIXI.Graphics();
  warmWash.beginFill(0xffefb0, 0.08);
  warmWash.drawRect(0, 0, width, height);
  warmWash.endFill();
  warmWash.blendMode = 'screen';
  sprites.addChild(warmWash);

  const beams = new PIXI.Graphics();
  beams.blendMode = 'screen';
  sprites.addChild(beams);

  const glowTexture = createGlowTexture(PIXI);
  const glow = new PIXI.Sprite(glowTexture);
  const baseGlowScale = Math.max(width, height) / 360;
  glow.anchor.set(0.5);
  glow.blendMode = 'screen';
  glow.alpha = 0.76;
  sprites.addChild(glow);

  const sourceGlow = new PIXI.Graphics();
  sourceGlow.beginFill(0xfff8cf, 0.7);
  sourceGlow.drawCircle(0, 0, 18);
  sourceGlow.endFill();
  sourceGlow.beginFill(0xffffff, 0.86);
  sourceGlow.drawCircle(0, 0, 8);
  sourceGlow.endFill();
  sourceGlow.blendMode = 'screen';
  sprites.addChild(sourceGlow);

  const specks: LightSpeck[] = Array.from({ length: 13 }, (_, index) => {
    const graphic = new PIXI.Graphics();
    const radius = 1.5 + (index % 4) * 0.55;

    graphic.beginFill(0xfff4bd, 0.86);
    graphic.drawCircle(0, 0, radius);
    graphic.endFill();
    graphic.lineStyle(1, 0xfff4bd, 0.45);
    graphic.moveTo(-radius * 2.2, 0);
    graphic.lineTo(radius * 2.2, 0);
    graphic.moveTo(0, -radius * 2.2);
    graphic.lineTo(0, radius * 2.2);
    graphic.blendMode = 'screen';
    sprites.addChild(graphic);

    return {
      graphic,
      homeX: width * (0.12 + Math.random() * 0.78),
      homeY: height * (0.16 + Math.random() * 0.72),
      phase: Math.random() * Math.PI * 2,
      radius,
    };
  });

  const animate = () => {
    const time = (app?.ticker.lastTime ?? performance.now()) * 0.001;
    const sourceX = width * (0.72 + Math.sin(time * 0.34) * 0.035);
    const sourceY = height * (0.2 + Math.cos(time * 0.28) * 0.035);
    const glowScale = baseGlowScale * (1 + Math.sin(time * 1.4) * 0.055);

    drawLightBeams(beams, width, height, sourceX, sourceY, time);

    glow.x = sourceX;
    glow.y = sourceY;
    glow.scale.set(glowScale);
    glow.alpha = 0.64 + Math.sin(time * 1.55) * 0.08;

    sourceGlow.x = sourceX;
    sourceGlow.y = sourceY;
    sourceGlow.alpha = 0.72 + Math.sin(time * 2.1) * 0.13;
    sourceGlow.scale.set(1 + Math.sin(time * 1.9) * 0.08);

    specks.forEach((speck, index) => {
      const speckTime = time + speck.phase;
      const alpha = 0.36 + (Math.sin(speckTime * 1.8) + 1) * 0.24;
      const scale = 0.72 + (Math.sin(speckTime * 2.1) + 1) * 0.28;

      speck.graphic.x =
        speck.homeX + Math.sin(speckTime * 0.65) * (10 + index);
      speck.graphic.y =
        speck.homeY + Math.cos(speckTime * 0.48) * (8 + speck.radius * 3);
      speck.graphic.alpha = alpha;
      speck.graphic.scale.set(scale);
      speck.graphic.rotation = speckTime * 0.4;
    });
  };

  animate();

  let cleanedUp = false;
  const cleanup = () => {
    if (cleanedUp) return;
    cleanedUp = true;

    app?.ticker?.remove?.(animate);
    if (glowTexture && glowTexture !== PIXI.Texture.WHITE) {
      glowTexture.destroy(true);
    }
  };

  if (app) {
    app.ticker.add(animate);
  }
  sprites.cleanup = cleanup;

  return sprites;
};
